import { memo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { Card } from './Card';

interface CardModalProps {
  card: Card;
  onClose: () => void;
}

const transitionOverlay = { duration: 0.5, ease: [0.32, 0.72, 0, 1] };

export const CardModal = memo<CardModalProps>(({ card, onClose }) => {
  const { t } = useTranslation();
  
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0 }}
      layoutId={`card-container-${card.id}`}
      onClick={handleOverlayClick}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-5 max-[640px]:p-3"
      style={{ willChange: "opacity" }}
      transition={transitionOverlay}
    >
      <motion.div
        layoutId={`card-${card.id}`}
        className="w-full max-w-[400px] aspect-[1.6] rounded-3xl p-6 shadow-[0_12px_32px_rgba(0,0,0,0.2)]"
        style={{
          background: `linear-gradient(135deg, ${card.gradient.join(', ')})`
        }}
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        transition={{
          delay: 0.5,
          duration: 0.5,
          ease: [0.25, 0.1, 0.25, 1],
        }}
      >
        <div 
          className="h-full flex flex-col opacity-100 relative z-[1]"
          style={{
            transformStyle: 'preserve-3d',
            transform: 'translateZ(1px)',
            WebkitBackfaceVisibility: 'visible',
            backfaceVisibility: 'visible'
          }}
        >
          <div 
            className="absolute -inset-5 rounded-3xl -z-[1]"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 50%, transparent 100%)'
            }}
          />
          
          <div className="flex flex-col" style={{ gap: '8px' }}>
            <div className="flex justify-between items-center w-full">
              <span className="text-sm font-medium m-0 opacity-90">
                {t('HOME_SCREEN.PREMIUM_DEALS.APY')}
              </span>
              <img 
                src={card.network_logo_url} 
                alt={t('HOME_SCREEN.PREMIUM_DEALS.NETWORK')} 
                className="w-6 h-6 rounded-full"
              />
            </div>
            <h1 
              className="font-semibold m-0 tracking-[-0.5px] w-full whitespace-nowrap"
              style={{
                fontSize: 'clamp(16px, 8vw, 32px)',
                transformOrigin: 'left'
              }}
            >
              {card.balance}
            </h1>
          </div>

          <div 
            className="font-semibold text-white text-left opacity-90 flex items-center w-full whitespace-nowrap my-4"
            style={{
              fontSize: 'clamp(12px, 4vw, 18px)',
              gap: '8px',
              transformOrigin: 'left'
            }}
          >
            <img src={card.logo_url} alt={t('HOME_SCREEN.PREMIUM_DEALS.TOKEN_TYPE')} className="w-6 h-6 rounded-full" />
            <span>{card.type}</span>
          </div>

          <div className="flex justify-between items-end mt-auto">
            <div className="bg-white/15 px-3 py-2 rounded-xl flex flex-col" style={{ gap: '4px' }}>
              <span className="text-xs font-medium text-white opacity-90">
                {card.cardNumber}
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
});

CardModal.displayName = 'CardModal';

